PS.calculateAllCurveValues = function (match, baseFlow) {
  const curve = match.curve;
  const correction = match.correction || PS.getFluidCorrection(curve);

  let waterHead;
  let waterPower;

  if (match.surface) {
    waterHead = PS.evaluateSurfaceHead(match.surface, baseFlow, match.selectedTrimMm, match.selectedSpeedRpm, match.curveQMax);
    waterPower = PS.evaluateSurfacePower(match.surface, baseFlow, match.selectedTrimMm, match.selectedSpeedRpm, match.curveQMax);
  } else {
    waterHead = PS.calculatePolynomial(PS.getHeadCoefficients(curve), baseFlow) * match.headRatio;
    waterPower = PS.calculatePolynomial(PS.getPowerCoefficients(curve), baseFlow) * match.powerRatio;
  }

  waterHead = PS.applyMaterialHeadFactor(waterHead, baseFlow, match.curveQMax, curve);

  // Unlike flow/efficiency, the viscosity head correction is not flat - it
  // varies with how far this point sits from the curve's own BEP flow (see
  // PS.getFluidCorrection), so it must be recomputed per point rather than
  // reusing one factor for the whole curve.
  const head = waterHead * correction.headCorrectionFactor(baseFlow);

  // Physical operating flow = baseFlow * flowRatio (= flow / cq at the surface).
  const physicalFlow = match.surface ? baseFlow * correction.cq : baseFlow * match.flowRatio * correction.cq;
  const npshr = PS.computeNpshr(curve, match.surface, {
    baseFlow,
    physicalFlow,
    speedRpm: match.selectedSpeedRpm,
    trimMm: match.selectedTrimMm,
    speedRatio: match.speedRatio,
    correction,
    // This function works in base-curve-flow terms, so the affinity fallback
    // (non-SC per-curve NPSHr) pulls flow back with baseFlow / speedRatio.
    npshRefFlow: baseFlow / match.speedRatio
  });

  // Derived from head and power (TAPS's own documented formula) rather than
  // the separately-fitted efficiency polynomial, so it can't drift above
  // 100% independently of what the head/power curves actually say.
  const waterEfficiency = PS.calculateEfficiencyFromHeadPower(baseFlow, waterHead, waterPower);
  const efficiency = waterEfficiency !== null
    ? Math.min(100, waterEfficiency * correction.cn)
    : null;

  // Power is derived from the corrected flow/head/efficiency and specific
  // gravity (the standard's own metric power formula) rather than scaling
  // the catalogued water-power polynomial directly.
  const correctedFlowLps = baseFlow * match.flowRatio * correction.cq;
  const power = (efficiency && efficiency > 0)
    ? (correctedFlowLps * 3.6) * head * correction.density / (367 * (efficiency / 100))
    : null;

  return {
    head,
    power,
    npshr,
    efficiency
  };
};

PS.buildAdjustedCurvePoints = function (match, mode) {
  const points = [];
  const correction = match.correction || PS.getFluidCorrection(match.curve);
  const maxFlow = match.surface
    ? match.curveQMax * correction.cq
    : PS.getCurveQMax(match.curve) * match.flowRatio * correction.cq;
  const minFlow = match.surface
    ? 0
    : PS.getCurveChartMinFlow(match.curve) * match.flowRatio * correction.cq;
  let npshMinFlow;
  let npshMaxFlow = Infinity;
  if (PS.usesCpSuctionNpshr(match.curve)) {
    // TAPS starts the NPSHr line at Qdiv * Qmult ≈ 0 (first chart division).
    // For per-curve data use npshrValidFromLps scaled to operating conditions;
    // for CP Suction Constants there is no lower bound — start at minFlow.
    const perCurveCoeffs = PS.getNpshCoefficients(match.curve);
    const hz = PS.unitSettings?.frequency || 50;
    const csc = match.curve?.familyMetadata?.cpSuctionConstants?.[String(hz)];
    if (perCurveCoeffs) {
      const validFrom = match.curve?.applicationRange?.npshrValidFromLps;
      npshMinFlow = Number.isFinite(validFrom) && validFrom > 0
        ? validFrom * match.flowRatio * correction.cq
        : minFlow;
      // npshrValidToLps bounds the polynomial fit - fall back to CP Suction
      // nlu when not set, since the per-curve fit uses the same test data range.
      const validTo = match.curve?.applicationRange?.npshrValidToLps;
      if (Number.isFinite(validTo) && validTo > 0) {
        npshMaxFlow = validTo * match.flowRatio * correction.cq;
      } else if (csc && Number.isFinite(csc.nlu) && csc.nlu > 0) {
        npshMaxFlow = csc.nlu * correction.cq;
      }
    } else {
      npshMinFlow = minFlow;
      if (csc && Number.isFinite(csc.nlu) && csc.nlu > 0) {
        npshMaxFlow = csc.nlu * correction.cq;
      }
    }
  } else if (match.surface) {
    const surfaceMin = PS.evaluateSurfaceNpshMinFlow(match.surface, match.selectedSpeedRpm, match.curveQMax, match.selectedTrimMm) * correction.cq;
    const recRangeSurf = PS.getRecommendedOperatingRangeLps(match);
    const recMinSurf = recRangeSurf ? recRangeSurf.minLps * correction.cq : 0;
    npshMinFlow = Math.max(surfaceMin, recMinSurf);
  } else {
    // Use npshrValidFromLps (the minimum flow from actual test data) if present,
    // scaled to operating conditions the same way minFlow is.
    const validFrom = match.curve?.applicationRange?.npshrValidFromLps;
    const validFromOperating = Number.isFinite(validFrom) && validFrom > 0
      ? validFrom * match.flowRatio * correction.cq
      : minFlow;
    const recRange = PS.getRecommendedOperatingRangeLps(match);
    const recMin = recRange ? recRange.minLps * correction.cq : 0;
    npshMinFlow = Math.max(validFromOperating, recMin);

    // npshrValidToLps bounds the NPSHr data/fit the same way validFromLps
    // bounds it from below - some domestic pumps (Onga 112) only publish
    // NPSHr over part of the head curve's full flow range (e.g. 50-250 lpm
    // out of a 0-320 lpm head curve), so without this the NPSHr line would
    // draw all the way to the pump's full qMax, past where any real data
    // backs it.
    const validTo = match.curve?.applicationRange?.npshrValidToLps;
    if (Number.isFinite(validTo) && validTo > 0) {
      npshMaxFlow = validTo * match.flowRatio * correction.cq;
    }
  }

  for (let i = 0; i <= 80; i += 1) {
    const flow = (maxFlow * i) / 80;
    const baseFlow = match.surface ? flow / correction.cq : flow / (match.flowRatio * correction.cq);
    const values = PS.calculateAllCurveValues(match, baseFlow);

    let y = null;

    if (mode === 'head' || mode === 'power') {
      y = mode === 'head' ? PS.headFromBase(values.head) : PS.powerFromBase(values.power);
    }

    if (mode === 'npsh' && flow >= npshMinFlow && flow <= npshMaxFlow) {
      y = values.npshr !== null
        ? PS.headFromBase(values.npshr)
        : null;
    }

    if (mode === 'efficiency' && flow >= minFlow) {
      y = values.efficiency;
    }

    if (y === null || !Number.isFinite(y)) {
      continue;
    }

    // Mirrors TAPS's own efficiency plot, which only draws points where the
    // computed efficiency is at least 10% - it has no flow-based floor, just
    // this value-based one, so very low efficiencies near zero flow are
    // suppressed rather than drawn dropping to 0.
    if (mode === 'efficiency' && y < 10) {
      continue;
    }

    // The npshBase affinity fallback (single-speed fitted curve, scaled per
    // pump) extrapolates outside its fitted flow range near the edges of the
    // curve and can swing negative there - drop those points rather than
    // plot a physically impossible NPSHr.
    if (mode === 'npsh' && y <= 0) {
      continue;
    }

    points.push({
      x: PS.flowFromBase(flow),
      y,
      flow: PS.flowFromBase(flow),
      head: PS.headFromBase(values.head),
      power: PS.powerFromBase(values.power),
      npshr: values.npshr !== null ? PS.headFromBase(values.npshr) : null,
      efficiency: values.efficiency
    });
  }

  return points;
};

// System curve overlay (Advanced mode). The user supplies the static (lift)
// head; the resistance coefficient K is fitted so the parabola
// H = staticHead + K*Q^2 passes through the pump's duty point. Its crossing
// with the pump head curve is the true operating point (see
// getSystemCurveOperatingPoint). Points are returned in display units, matching
// buildAdjustedCurvePoints, over the same flow domain as the head curve so the
// two lines share an x-range. Returns [] (draw nothing) for a missing/absurd
// static head - staticHead >= dutyHead has no physical fit - rather than throw.
PS.getSystemCurveCoefficients = function (match) {
  const sys = PS.state.systemCurve;
  if (!sys || sys.staticHeadBase == null) return null;

  const staticHead = Number(sys.staticHeadBase);
  const dutyFlow = Number(match.flow);
  const dutyHead = Number(match.requiredHead);

  if (!Number.isFinite(staticHead) || !Number.isFinite(dutyFlow) || !Number.isFinite(dutyHead)) return null;
  if (dutyFlow <= 0 || staticHead >= dutyHead) return null;

  const correction = match.correction || PS.getFluidCorrection(match.curve);
  const maxFlow = match.surface
    ? match.curveQMax * correction.cq
    : PS.getCurveQMax(match.curve) * match.flowRatio * correction.cq;

  if (!Number.isFinite(maxFlow) || maxFlow <= 0) return null;

  return {
    staticHead,
    k: (dutyHead - staticHead) / (dutyFlow * dutyFlow),
    maxFlow,
    correction
  };
};

PS.buildSystemCurvePoints = function (match) {
  const coeffs = PS.getSystemCurveCoefficients(match);
  if (!coeffs) return [];

  const points = [];

  for (let i = 0; i <= 80; i += 1) {
    const flow = (coeffs.maxFlow * i) / 80;
    const head = coeffs.staticHead + coeffs.k * flow * flow;

    points.push({
      x: PS.flowFromBase(flow),
      y: PS.headFromBase(head),
      flow: PS.flowFromBase(flow),
      head: PS.headFromBase(head),
      power: null,
      npshr: null,
      efficiency: null
    });
  }

  return points;
};

// The operating point: where the pump head curve crosses the system curve.
// Scans the pump head (evaluated in base units, exactly as buildAdjustedCurvePoints
// derives baseFlow) against the fitted parabola and linear-interpolates the first
// sign change of (pumpHead - systemHead). Returns display-unit {flow, head} or null.
PS.getSystemCurveOperatingPoint = function (match) {
  const coeffs = PS.getSystemCurveCoefficients(match);
  if (!coeffs) return null;

  const { staticHead, k, maxFlow, correction } = coeffs;
  const steps = 200;
  let prevFlow = null;
  let prevDiff = null;

  for (let i = 0; i <= steps; i += 1) {
    const flow = (maxFlow * i) / steps;
    const baseFlow = match.surface ? flow / correction.cq : flow / (match.flowRatio * correction.cq);
    const values = PS.calculateAllCurveValues(match, baseFlow);
    const pumpHead = values.head;

    if (!Number.isFinite(pumpHead)) {
      prevFlow = null;
      prevDiff = null;
      continue;
    }

    const systemHead = staticHead + k * flow * flow;
    const diff = pumpHead - systemHead;

    if (prevDiff !== null && prevDiff !== diff &&
        ((prevDiff >= 0 && diff <= 0) || (prevDiff <= 0 && diff >= 0))) {
      const t = prevDiff / (prevDiff - diff);
      const crossFlow = prevFlow + t * (flow - prevFlow);
      const crossHead = staticHead + k * crossFlow * crossFlow;
      return { flow: PS.flowFromBase(crossFlow), head: PS.headFromBase(crossHead) };
    }

    prevFlow = flow;
    prevDiff = diff;
  }

  return null;
};

// For N identical pumps running in parallel, each pump independently passes
// the same flow at a given head, so the combined curve at that head is just
// the single-pump curve's flow (and, for power, its power draw) multiplied
// by N - head itself is unchanged since it's shared rather than summed.
PS.buildParallelCurvePoints = function (match, mode, pumpCount) {
  const basePoints = PS.buildAdjustedCurvePoints(match, mode);

  if (pumpCount <= 1) return basePoints;

  return basePoints.map(point => ({
    ...point,
    x: point.x * pumpCount,
    flow: point.flow * pumpCount,
    y: mode === 'power' ? point.y * pumpCount : point.y,
    power: mode === 'power' ? point.power * pumpCount : point.power
  }));
};

PS.hasUsableNpshData = function (match) {
  if (match.curve?.showNpshCurve === false) return false;

  if (match.surface) {
    if (!match.surface.NC && !match.surface.npshBase) return false;
  } else {
    const coeffs = PS.getNpshCoefficients(match.curve);
    if (!coeffs || !Array.isArray(coeffs)) return false;
  }

  return PS.buildAdjustedCurvePoints(match, 'npsh').some(point =>
    Number.isFinite(point.y) &&
    point.y > 0 &&
    point.y < 100
  );
};

// Onga OME pumps have no fitted power polynomial (coefficients null/empty),
// which makes calculateAllCurveValues return power/efficiency as null for
// every point - mirrors PS.hasUsableNpshData's shape so the power and
// NPSHr tabs are suppressed the same way.
PS.hasUsablePowerData = function (match) {
  if (match.surface) {
    if (!match.surface.PC) return false;
  } else {
    const coeffs = PS.getPowerCoefficients(match.curve);
    if (!coeffs || !Array.isArray(coeffs) || coeffs.length === 0) return false;
  }

  return PS.buildAdjustedCurvePoints(match, 'power').some(point =>
    Number.isFinite(point.y) && point.y > 0
  );
};

// Efficiency is derived from head and power together (see
// calculateAllCurveValues), so it can never be usable without power data.
PS.hasUsableEfficiencyData = function (match) {
  if (!PS.hasUsablePowerData(match)) return false;

  return PS.buildAdjustedCurvePoints(match, 'efficiency').some(point =>
    Number.isFinite(point.y) && point.y > 0
  );
};

PS.getSuggestedNpshMax = function (matches, visibleIndexes) {
  let maxNpsh = 0;

  matches.forEach((match, index) => {
    if (!visibleIndexes.has(index)) return;
    if (!PS.hasUsableNpshData(match)) return;

    PS.buildAdjustedCurvePoints(match, 'npsh').forEach(point => {
      if (Number.isFinite(point.y)) {
        maxNpsh = Math.max(maxNpsh, point.y);
      }
    });
  });

  if (!maxNpsh) {
    return 15;
  }

  return Math.ceil(maxNpsh * 1.1);
};

PS.getSuggestedPowerMax = function (matches, visibleIndexes) {
  let maxPower = 0;

  matches.forEach((match, index) => {
    if (!visibleIndexes.has(index)) return;

    PS.buildAdjustedCurvePoints(match, 'power').forEach(point => {
      if (Number.isFinite(point.y)) {
        maxPower = Math.max(maxPower, point.y);
      }
    });
  });

  if (!maxPower) {
    return 15;
  }

  return Math.ceil(maxPower * 1.1);
};

// Chart.js has no native concept of confining one axis to a sub-region of
// the chart area - every scale always spans the full plot height. TAPS's own
// curve sheets draw the power/NPSHr axes squeezed into a low band near the
// bottom, and the efficiency axis stacked directly above that (occupying the
// rest of the height), so this plugin reaches into each scale's computed
// pixel range after layout and shrinks it to its assigned fraction - anchored
// at the bottom for power/NPSHr, at the top for efficiency - then re-runs
// configure() so both the tick positions and the plotted points pick up the
// confined range.
PS.confineAxisPlugin = {
  id: 'confineAxis',
  afterLayout(chart) {
    const config = chart.options.plugins?.confineAxis;

    if (!config) return;

    Object.entries(config).forEach(([scaleId, spec]) => {
      const scale = chart.scales[scaleId];

      if (!scale) return;

      const { fraction, anchor = 'bottom' } = typeof spec === 'number' ? { fraction: spec } : spec;
      const fullHeight = scale.bottom - scale.top;

      if (anchor === 'top') {
        scale.bottom = scale.top + (fullHeight * fraction);
      } else {
        scale.top = scale.bottom - (fullHeight * fraction);
      }

      scale.height = scale.bottom - scale.top;
      scale.configure();
    });

    // Power and efficiency are confined to non-overlapping vertical bands
    // (above), but Chart.js still lays them out as two separate side-by-side
    // columns since it has no idea they never actually overlap - wasting
    // width and pushing anything further out (NPSHr) away from the chart.
    // Merge them into one shared column and shift NPSHr inward to close the
    // gap that frees up.
    const power = chart.scales.yPower;
    const efficiency = chart.scales.yEfficiency;

    if (power && efficiency && power.position === 'right' && efficiency.position === 'right') {
      const sharedWidth = Math.max(power.right - power.left, efficiency.right - efficiency.left);
      const sharedLeft = Math.min(power.left, efficiency.left);
      const sharedRight = sharedLeft + sharedWidth;
      const outerRight = Math.max(power.right, efficiency.right);
      // Leave a small gutter for PS.stackedTitlePlugin's manually-drawn
      // label between the merged column and whatever sits outside it.
      const labelGutter = 16;
      const freed = outerRight - sharedRight - labelGutter;

      [power, efficiency].forEach(scale => {
        scale.left = sharedLeft;
        scale.right = sharedRight;
        scale.width = sharedWidth;
      });

      if (freed > 0.5) {
        Object.values(chart.scales).forEach(scale => {
          if (scale !== power && scale !== efficiency && scale.position === 'right' && scale.left >= outerRight - 0.01) {
            scale.left -= freed;
            scale.right -= freed;
          }
        });
      }
    }
  }
};

// Draws the power/efficiency axis titles manually, stacked one above the
// other in a single shared column right next to their own tick numbers -
// rather than Chart.js's own per-axis title, which would give each its own
// separate column, or naively using the rightmost axis on the chart (which
// would push the label out past NPSH's separate column instead of sitting
// next to power/efficiency's own ticks). Each label is vertically centred
// within its own scale's (possibly confined, see PS.confineAxisPlugin)
// top/bottom range, so when both axes are present "Efficiency %" sits
// directly above "Power kW" with no gap.
PS.stackedTitlePlugin = {
  id: 'stackedTitle',
  afterDraw(chart) {
    const config = chart.options.plugins?.stackedTitle;

    if (!config) return;

    const scales = config.map(({ scaleId }) => chart.scales[scaleId]).filter(Boolean);

    if (!scales.length) return;

    const x = Math.max(...scales.map(scale => scale.right)) + 8;
    const { ctx } = chart;

    ctx.save();
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    config.forEach(({ scaleId, text, color }) => {
      const scale = chart.scales[scaleId];

      if (!scale) return;

      const y = (scale.top + scale.bottom) / 2;

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(Math.PI / 2);
      ctx.fillStyle = color || '#666';
      ctx.fillText(text, 0, 0);
      ctx.restore();
    });

    ctx.restore();
  }
};

PS.getMaxVisibleFlow = function (matches, visibleIndexes) {
  let maxFlow = 0;

  matches.forEach((match, index) => {
    if (!visibleIndexes.has(index)) return;

    const correction = match.correction || PS.getFluidCorrection(match.curve);
    const flow = match.surface
      ? match.curveQMax * correction.cq
      : PS.getCurveQMax(match.curve) * match.flowRatio * correction.cq;

    maxFlow = Math.max(maxFlow, PS.flowFromBase(flow));
  });

  return maxFlow;
};

PS.buildScales = function (modes, matches, visibleIndexes, parallelCount) {
  const scales = {
    x: {
      title: {
        display: true,
        text: `Flow ${PS.getFlowUnitText()}`
      }
    }
  };

  if (Array.isArray(matches) && visibleIndexes) {
    const maxFlow = PS.getMaxVisibleFlow(matches, visibleIndexes) * (parallelCount || 1);

    if (maxFlow > 0) {
      scales.x.max = maxFlow * 1.1;
    }
  }

  if (modes.has('head')) {
    scales.yHead = {
      type: 'linear',
      position: 'left',
      title: {
        display: true,
        text: `Head ${PS.getHeadUnitText()}`,
        color: PS.chartModeConfig.head.color
      },
      beginAtZero: true
    };
  }

  if (modes.has('power')) {
    scales.yPower = {
      type: 'linear',
      position: 'right',
      // Title is drawn manually by PS.stackedTitlePlugin, stacked with the
      // efficiency axis's title in the same column, rather than Chart.js's
      // own per-axis title (which would put it in its own separate column).
      title: {
        display: false
      },
      beginAtZero: true,
      suggestedMax: PS.getSuggestedPowerMax(matches, visibleIndexes),
      grid: {
        drawOnChartArea: false
      }
    };
  }

  if (modes.has('efficiency')) {
    scales.yEfficiency = {
      type: 'linear',
      position: 'right',
      // See the note on yPower above - title drawn manually, stacked above
      // it, and stacked into the same shared column.
      title: {
        display: false
      },
      min: 0,
      max: 100,
      // The 0 tick sits right at the boundary with the power/NPSHr band
      // below it, landing on top of NPSHr's own topmost (max-scale) tick -
      // hide just that one label to keep the boundary clean.
      ticks: {
        callback(value) {
          return value === 0 ? '' : value;
        }
      },
      grid: {
        drawOnChartArea: false
      }
    };
  }

  if (modes.has('npsh')) {
    scales.yNpsh = {
      type: 'linear',
      position: 'right',
      title: {
        display: true,
        text: `NPSHr ${PS.getHeadUnitText()}`,
        color: PS.chartModeConfig.npsh.color
      },
      beginAtZero: true,
      suggestedMax: PS.getSuggestedNpshMax(matches, visibleIndexes),
      grid: {
        drawOnChartArea: false
      }
    };
  }

  return scales;
};

PS.curveLabelPlugin = function () {
  return {
    id: 'curveLabelPlugin',

    afterDatasetsDraw(chart) {
      if (chart.canvas.id === 'curve-export-canvas') {
        return;
      }

      const { ctx } = chart;

      ctx.save();
      ctx.font = '12px sans-serif';
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';

      chart.data.datasets.forEach((dataset, datasetIndex) => {
        if (!dataset.showLine || dataset.label === 'Duty point') return;
        if (!dataset.data || dataset.data.length < 2) return;

        if (chart.canvas.id === 'pump-chart' && !String(dataset.label).includes('Head')) {
          return;
        }

        // Single-curve charts (one pump, one mode set, no VSD overlay) use
        // the same colour for a curve as its axis title - see the colour
        // wiring in PS.buildScales/PS.stackedTitlePlugin - so labelling the
        // curve itself would just repeat that. Only multi-curve datasets
        // (several pumps or several VSD speeds, identified by the
        // "<curve> - <mode>" label format) and the Recommended range
        // markers (which have no axis of their own) still need a label.
        const isMultiCurve = String(dataset.label).includes(' - ');

        if (!isMultiCurve && dataset.label !== 'Recommended range') {
          return;
        }

        const meta = chart.getDatasetMeta(datasetIndex);
        const last = meta.data[meta.data.length - 1];
        const prev = meta.data[meta.data.length - 2];

        if (!last) return;

        const label = String(dataset.label).split(' - ')[0];

        ctx.fillStyle = dataset.borderColor || '#000';

        // Lift the label off the line itself rather than sitting centred on
        // top of it (as Power's near-flat line did) - nudge perpendicular to
        // the line's local direction at its endpoint, biased upward so it
        // doesn't just flip to sitting under the line instead.
        let yOffset = -8;

        if (prev) {
          const dx = last.x - prev.x;
          const dy = last.y - prev.y;
          const len = Math.hypot(dx, dy) || 1;
          let ny = -dx / len;

          if (ny > 0) ny = -ny;
          yOffset = ny * 9;
        }

        const y = last.y + yOffset;

        // Default to drawing the label to the right of the line's last
        // point, but if that would run past the plot area (and into the
        // axis ticks/labels beside it, as efficiency's curve does when it
        // ends near the top-right corner) flip it to grow back to the left
        // instead - same trick already used for Recommended range below.
        const fitsOnRight = last.x + 6 + ctx.measureText(label).width <= chart.chartArea.right;

        if (label === 'Recommended range' || !fitsOnRight) {
          ctx.textAlign = 'right';
          ctx.fillText(label, last.x - 6, y);
          ctx.textAlign = 'left';
        } else {
          ctx.fillText(label, last.x + 6, y);
        }
      });

      ctx.restore();
    }
  };
};

// Re-evaluates each user-added extra curve spec (a speed/frequency only -
// same trim, same duty point) against the currently selected match, so the
// overlay always reflects whatever trim/duty/motor the main curve is
// currently showing rather than going stale after the user adjusts those.
PS.getExtraCurveMatches = function (match) {
  if (!match || match.controlType !== 'vsd') return [];

  const motor = PS.getEffectiveMotor(match);

  return (PS.state.extraCurveSpecs || [])
    .filter(spec => !spec.pending)
    .map(spec => PS.evaluateAdjustedCurve(match.curve, match.flow, match.requiredHead, {
      frequencyHz: spec.valueHz,
      poles: motor.poles,
      motor,
      trimMm: match.selectedTrimMm,
      controlType: match.controlType
    }))
    .filter(Boolean);
};

// The extra duty-point rows the customer has actually filled in, as plottable
// flow/head pairs. Their values are already in display units (typed straight
// into the adjustment panel), so no base conversion applies. A freshly added
// row starts blank and Number('') is 0, not NaN - without the explicit blank
// check an unfinished row would plot (and print) a phantom marker at the
// origin. The original row index is kept so the "Duty point N" numbering
// follows the adjustment panel's own row order, and so a given row keeps its
// colour while a neighbouring row is still being filled in. Shared by the
// chart and the printed curve sheet so the two can't disagree about which
// points exist.
PS.getFilledDutyPoints = function () {
  return (PS.state.dutyPoints || []).reduce((points, point, index) => {
    const flow = Number(point.flow);
    const head = Number(point.head);
    const blank = value => value === '' || value === null || value === undefined;

    if (blank(point.flow) || blank(point.head)) return points;
    if (!Number.isFinite(flow) || !Number.isFinite(head)) return points;

    points.push({ index, flow, head });
    return points;
  }, []);
};

// Draws the detail-view chart for a single pump, additionally overlaying any
// user-added VSD speed/frequency curves for that same pump (see
// PS.getExtraCurveMatches) - the duty-results and model-search charts don't
// support extra curves, so they fall straight through to a single curve
// unchanged.
// The printed curve sheet ('export') renders through here too, so the sheet
// carries whatever extra curves are on screen instead of silently dropping
// them (PS.generateCurveSheet).
PS.drawDetailChart = function (canvasId, chartKey, match, modes) {
  const extras = (chartKey === 'detail' || chartKey === 'export') ? PS.getExtraCurveMatches(match) : [];
  const matches = [match, ...extras];

  PS.drawChart(canvasId, chartKey, matches, new Set(matches.map((_, i) => i)), modes, 0);
};

PS.drawChart = function (canvasId, chartKey, matches, visibleIndexes, modes, selectedIndex) {
  const canvas = document.getElementById(canvasId);

  if (!canvas) {
    return;
  }

  if (PS.charts[chartKey]) {
    PS.charts[chartKey].destroy();
  }

  const selectedMatch = matches[selectedIndex];

  if (!selectedMatch) {
    return;
  }

  const datasets = [];

  matches.forEach((match, matchIndex) => {
    if (!visibleIndexes.has(matchIndex)) {
      return;
    }

    modes.forEach(mode => {
      if (mode === 'npsh' && !PS.hasUsableNpshData(match)) {
        return;
      }

      const config = PS.chartModeConfig[mode];
      const points = PS.buildAdjustedCurvePoints(match, mode);

      if (!points.length) {
        return;
      }

      // The duty-results chart can show several pumps at once, and the detail
      // view (and the curve sheet printed from it) can show one pump at
      // several VSD speeds at once, so either of those needs its own colour
      // per curve to stay distinguishable; a plain single-curve
      // detail/model/export chart only ever shows one curve set, so it uses
      // the same per-mode colours as the chart-mode toggle buttons.
      const isExport = chartKey === 'export';
      const isMultiCurve = matches.length > 1;
      const isSinglePump = chartKey !== 'duty' && !isMultiCurve;
      const baseColor = isSinglePump ? config.color : PS.getMatchColor(matchIndex);
      const focused = PS.state.focusedMatchIndex;
      const isFocused = chartKey === 'duty' && focused === matchIndex;
      const isFaded = chartKey === 'duty' && focused !== null && !isFocused;
      const curveLabel = chartKey === 'duty'
        ? PS.getPumpDisplayName(match)
        : PS.getSpeedDisplayLabel(match);

      // Southern Cross VSD overload indicator - only meaningful on the
      // single-pump detail view, where the Motor Selection card lets a
      // customer knowingly pick a motor smaller than the pump's own
      // worst-case power. PS.getSelectedMotorRatedKw returns null for every
      // other manufacturer/control type/view, so this is a no-op elsewhere.
      const overloadThreshold = (mode === 'power' && chartKey === 'detail')
        ? PS.getSelectedMotorRatedKw(match)
        : null;
      const overloadColor = '#c0392b';

      datasets.push({
        label: isSinglePump ? config.label : `${curveLabel} - ${config.label}`,
        data: points,
        showLine: true,
        pointRadius: 0,
        borderWidth: isExport ? 3 : (isFocused ? 4 : 2),
        borderColor: isFaded ? PS.withAlpha(baseColor, 0.25) : baseColor,
        backgroundColor: isFaded ? PS.withAlpha(baseColor, 0.25) : baseColor,
        borderDash: mode === 'efficiency' ? [5, 4] : [],
        yAxisID: config.yAxisID,
        // Colours the segment red once either endpoint reaches the selected
        // motor's rated power - since shaft power rises with flow across a
        // centrifugal curve, this reads as "red from the overload point
        // through to the end of the curve" rather than a single crossing.
        ...(overloadThreshold ? {
          segment: {
            borderColor: ctx => {
              const thresholdDisplay = PS.powerFromBase(overloadThreshold);
              const y0 = ctx.p0.parsed.y;
              const y1 = ctx.p1.parsed.y;
              return (y0 >= thresholdDisplay || y1 >= thresholdDisplay) ? overloadColor : baseColor;
            }
          }
        } : {})
      });
    });
  });

  // "Pumps in parallel" overlay - only meaningful for the single-pump
  // detail/model views (not the multi-pump duty comparison or export sheet),
  // and only for head, since each pump still draws its own power from its
  // own shaft - the unit's combined power is reported as a total figure
  // (see PS.getTotalParallelPowerKw) rather than a separate scaled curve.
  // NPSHr and efficiency are per-pump properties too, so they're untouched.
  const parallelCount = PS.state.parallelPumpCount || 1;

  if ((chartKey === 'detail' || chartKey === 'model') && parallelCount > 1 && matches.length === 1 && modes.has('head')) {
    for (let n = 2; n <= parallelCount; n += 1) {
      const config = PS.chartModeConfig.head;
      const points = PS.buildParallelCurvePoints(selectedMatch, 'head', n);

      if (!points.length) continue;

      const baseColor = PS.getMatchColor(n - 1);

      datasets.push({
        label: `${n} Pumps - ${config.label}`,
        data: points,
        showLine: true,
        pointRadius: 0,
        borderWidth: 2,
        borderDash: [8, 4],
        borderColor: baseColor,
        backgroundColor: baseColor,
        yAxisID: config.yAxisID
      });
    }
  }

  // Extra user-added duty point markers - independent of the curve/trim/speed,
  // just flow/head pairs plotted as dots so several operating points can be
  // compared on the same chart (e.g. current vs future duty). Drawn on the
  // printed curve sheet ('export') as well, so the sheet matches the screen.
  if (modes.has('head') && (chartKey === 'detail' || chartKey === 'model' || chartKey === 'export')) {
    // Offset past both the parallel-pump curves and any extra VSD curves, which
    // take PS.getMatchColor(1..n) themselves - without this the first extra duty
    // point lands on the same colour as the first extra curve beside it.
    const markerColorOffset = parallelCount + Math.max(0, matches.length - 1);

    PS.getFilledDutyPoints().forEach(({ index, flow, head }) => {
      datasets.push({
        label: `Duty point ${index + 2}`,
        data: [{ x: flow, y: head, flow, head, power: null, npshr: null, efficiency: null }],
        pointRadius: 6,
        borderColor: PS.getMatchColor(index + markerColorOffset),
        backgroundColor: PS.getMatchColor(index + markerColorOffset),
        yAxisID: 'yHead'
      });
    });
  }

  if (modes.has('head') && selectedMatch.isAutoDuty !== true && PS.state.dutyPointVisible !== false) {
    datasets.push({
      label: 'Duty point',
      data: [
        {
          x: PS.flowFromBase(selectedMatch.flow),
          y: PS.headFromBase(selectedMatch.requiredHead)
        }
      ],
      pointRadius: 6,
      borderColor: '#000',
      backgroundColor: '#000',
      yAxisID: 'yHead'
    });
  }

  // System curve overlay (Advanced mode only) - the fitted H = staticHead + K*Q^2
  // parabola plus its crossing with the pump curve (the operating point). Drawn
  // on the single-pump detail view and on the printed/export curve sheet, only
  // when a real duty was entered (the fit needs a duty point). Guarded off in
  // Basic mode and for the multi-pump duty comparison / model-BEP stand-in.
  if (
    PS.state.selectionMode === 'advanced' &&
    PS.state.systemCurve?.enabled &&
    modes.has('head') &&
    (chartKey === 'detail' || chartKey === 'export') &&
    matches.length === 1 &&
    selectedMatch.isAutoDuty !== true
  ) {
    const systemPoints = PS.buildSystemCurvePoints(selectedMatch);

    if (systemPoints.length) {
      datasets.push({
        label: 'System curve',
        data: systemPoints,
        showLine: true,
        pointRadius: 0,
        borderWidth: 2,
        borderDash: [10, 4],
        borderColor: '#e67e22',
        backgroundColor: '#e67e22',
        yAxisID: 'yHead'
      });

      const operatingPoint = PS.getSystemCurveOperatingPoint(selectedMatch);

      if (operatingPoint) {
        datasets.push({
          label: 'Operating point',
          data: [{
            x: operatingPoint.flow,
            y: operatingPoint.head,
            flow: operatingPoint.flow,
            head: operatingPoint.head,
            power: null,
            npshr: null,
            efficiency: null
          }],
          pointRadius: 6,
          pointStyle: 'rectRot',
          borderColor: '#e67e22',
          backgroundColor: '#e67e22',
          yAxisID: 'yHead'
        });
      }
    }
  }

  // Only drawn for single-curve charts (detail/model/export) - the duty
  // comparison chart can show several pumps at once, and a recommended
  // range per curve would just clutter that view.
  if (modes.has('head') && chartKey !== 'duty' && matches.length === 1) {
    const range = PS.getRecommendedOperatingRangeLps(selectedMatch);

    if (range) {
      const headPoints = PS.buildAdjustedCurvePoints(selectedMatch, 'head');
      const maxHead = headPoints.reduce((max, point) => Math.max(max, point.y || 0), 0);
      const topY = maxHead > 0 ? maxHead * 1.05 : 1;

      [range.minLps, range.maxLps].forEach(flowLps => {
        const x = PS.flowFromBase(flowLps);

        datasets.push({
          label: 'Recommended range',
          data: [{ x, y: 0 }, { x, y: topY }],
          showLine: true,
          pointRadius: 0,
          borderWidth: 1.5,
          borderDash: [6, 4],
          borderColor: '#c0392b',
          backgroundColor: '#c0392b',
          yAxisID: 'yHead'
        });
      });
    }
  }

  // Marks the selected motor's rated power on the Power axis, alongside the
  // segment-colour change above - same Southern Cross VSD detail-view-only
  // gating via PS.getSelectedMotorRatedKw.
  if (modes.has('power') && chartKey === 'detail' && matches.length === 1) {
    const motorRatedKw = PS.getSelectedMotorRatedKw(selectedMatch);

    if (motorRatedKw) {
      const thresholdDisplay = PS.powerFromBase(motorRatedKw);
      const powerPoints = PS.buildAdjustedCurvePoints(selectedMatch, 'power');
      const maxFlow = powerPoints.reduce((max, point) => Math.max(max, point.x || 0), 0);

      datasets.push({
        label: 'Selected motor rating',
        data: [{ x: 0, y: thresholdDisplay }, { x: maxFlow, y: thresholdDisplay }],
        showLine: true,
        pointRadius: 0,
        borderWidth: 1.5,
        borderDash: [6, 4],
        borderColor: '#c0392b',
        backgroundColor: '#c0392b',
        yAxisID: 'yPower'
      });
    }
  }

  PS.charts[chartKey] = new Chart(canvas, {
    type: 'scatter',
    data: {
      datasets
    },
    plugins: [
      PS.curveLabelPlugin(),
      PS.confineAxisPlugin,
      PS.stackedTitlePlugin
    ],
    options: {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          right: 60
        }
      },
      interaction: {
        intersect: false,
        mode: 'nearest'
      },
      plugins: {
        // Mirrors TAPS's curve sheets: power/NPSHr squeezed into a low band
        // near the bottom, efficiency stacked directly above that, filling
        // the rest of the height - see PS.confineAxisPlugin. Only confine
        // efficiency this way when power/NPSHr are actually sharing the
        // chart with it (the duty-results chart shows head+efficiency alone,
        // where efficiency should keep the full height).
        confineAxis: {
          ...(modes.has('power') ? { yPower: { fraction: 1 / 3, anchor: 'bottom' } } : {}),
          ...(modes.has('npsh') ? { yNpsh: { fraction: 1 / 3, anchor: 'bottom' } } : {}),
          ...(modes.has('efficiency') && (modes.has('power') || modes.has('npsh'))
            ? { yEfficiency: { fraction: 2 / 3, anchor: 'top' } }
            : {})
        },
        // Draws the Power/Efficiency axis titles stacked in one shared
        // column - see PS.stackedTitlePlugin.
        stackedTitle: [
          ...(modes.has('efficiency') ? [{ scaleId: 'yEfficiency', text: 'Efficiency %', color: PS.chartModeConfig.efficiency.color }] : []),
          ...(modes.has('power') ? [{ scaleId: 'yPower', text: `Power ${PS.getPowerUnitText()}`, color: PS.chartModeConfig.power.color }] : [])
        ],
        legend: {
          display: chartKey === 'export'
        },
        tooltip: {
          callbacks: {
            label(context) {
              const p = context.raw;

              if (!p || p.flow === undefined) {
                return context.dataset.label;
              }

              const rows = [
                `${context.dataset.label}`,
                `Flow: ${PS.formatNumber(p.flow)} ${PS.getFlowUnitText()}`,
                `Head: ${PS.formatNumber(p.head)} ${PS.getHeadUnitText()}`,
                `Power: ${PS.formatNumber(p.power)} ${PS.getPowerUnitText()}`
              ];

              if (p.npshr !== null) {
                rows.push(`NPSHr: ${PS.formatNumber(p.npshr)} ${PS.getHeadUnitText()}`);
              }

              if (p.efficiency !== null) {
                rows.push(`Efficiency: ${PS.formatNumber(p.efficiency)} %`);
              }

              return rows;
            }
          }
        }
      },
      scales: PS.buildScales(
        modes,
        matches,
        visibleIndexes,
        (chartKey === 'detail' || chartKey === 'model') ? parallelCount : 1
      )
    }
  });

  if (chartKey !== 'export' && chartKey !== 'model') {
    PS.saveSession();
  }
};